/**
 * Assemble a flat array of OpenSearch events into a structured trace.
 *
 * Returns an array of turns, each containing:
 * - userMessage: the user's message event
 * - spans: ordered LLM steps with request/response/tool events
 * - assistantMessage: the assistant's response message event
 */
export function assembleTrace(events) {
  // Separate feedback events from trace events
  const feedbackEvents = [];
  const traceMap = new Map();

  for (const event of events) {
    if (event.eventType === 'feedback') {
      feedbackEvents.push(event);
      continue;
    }

    const { traceId } = event;
    if (!traceId) continue;

    if (!traceMap.has(traceId)) {
      traceMap.set(traceId, { messages: [], spanEvents: [] });
    }

    const group = traceMap.get(traceId);

    if (event.eventType === 'message') {
      group.messages.push(event);
    } else {
      group.spanEvents.push(event);
    }
  }

  // Group feedback events by the messageId they target
  const feedbackByMessageId = new Map();
  for (const event of feedbackEvents) {
    const messageId = event.messageId;
    if (!messageId) continue;
    if (!feedbackByMessageId.has(messageId)) {
      feedbackByMessageId.set(messageId, []);
    }
    feedbackByMessageId.get(messageId).push({
      feedback: event.eventData.feedback,
      timestamp: event.timestamp,
    });
  }

  // Build turns from each trace group
  const turns = [];

  for (const [traceId, group] of traceMap) {
    const allEvents = [...group.messages, ...group.spanEvents];
    const traceType = allEvents.find((e) => e.traceType)?.traceType || 'conversation';

    const userMessage = group.messages.find((m) => m.role === 'user') || null;
    const assistantMessage = group.messages.find((m) => m.role === 'assistant') || null;

    // Group span events by spanId
    const spanMap = new Map();
    for (const event of group.spanEvents) {
      const { spanId } = event;
      if (!spanId) continue;

      if (!spanMap.has(spanId)) {
        spanMap.set(spanId, []);
      }
      spanMap.get(spanId).push(event);
    }

    // Build span objects
    const spans = [];
    for (const [spanId, spanEvents] of spanMap) {
      const request = spanEvents.find((e) => e.eventType === 'request') || null;
      const response = spanEvents.find((e) => e.eventType === 'response') || null;
      const toolOutputs = spanEvents.filter((e) => e.eventType === 'function_call_output');
      const toolErrors = spanEvents.filter((e) => e.eventType === 'function_call_error');
      const recoveries = spanEvents.filter((e) => e.eventType === 'missing_tool_output_recovery');
      const programs = spanEvents.filter((e) => e.eventType === 'program');
      const programOutputs = spanEvents.filter((e) => e.eventType === 'program_output');

      const earliestTimestamp = spanEvents.reduce(
        (min, e) => (e.timestamp < min ? e.timestamp : min),
        spanEvents[0].timestamp,
      );

      let durationMs = null;
      if (request?.timestamp && response?.timestamp) {
        durationMs = new Date(response.timestamp) - new Date(request.timestamp);
      }

      spans.push({
        spanId,
        request,
        response,
        toolOutputs,
        toolErrors,
        recoveries,
        programs,
        programOutputs,
        durationMs,
        earliestTimestamp,
      });
    }

    // Sort spans chronologically
    spans.sort((a, b) => (a.earliestTimestamp < b.earliestTimestamp ? -1 : 1));

    // Derive a label for each span based on what triggered it
    for (let i = 0; i < spans.length; i++) {
      if (i === 0) {
        spans[i].label = 'Initial request';
        spans[i].triggerTools = null;
      } else {
        const prev = spans[i - 1];
        const toolNames = [
          ...prev.toolOutputs.map((t) => t.eventData?.name),
          ...prev.toolErrors.map((t) => t.eventData?.name),
        ].filter(Boolean);

        spans[i].triggerTools = toolNames;
        spans[i].label = toolNames.length > 0 ? `Follow-up from ${toolNames.join(', ')}` : 'Follow-up';
      }

      // Mark if this is the final span (produced the answer, no tool calls)
      const hasToolCalls = spans[i].toolOutputs.length > 0 || spans[i].toolErrors.length > 0;
      if (!hasToolCalls && i === spans.length - 1) {
        spans[i].label = i === 0 ? 'Direct response' : 'Composing response';
      }
    }

    const assistantMessageId = assistantMessage?.messageId;
    const turnFeedback = assistantMessageId ? feedbackByMessageId.get(assistantMessageId) || [] : [];

    turns.push({
      traceId,
      traceType,
      userMessage,
      assistantMessage,
      spans,
      feedbackEvents: turnFeedback,
      timestamp: userMessage?.timestamp || assistantMessage?.timestamp || spans[0]?.earliestTimestamp,
    });
  }

  // Sort turns chronologically
  turns.sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1));

  return turns;
}
